import * as React from 'react';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';
import { useState } from 'react';
import { TipsterRoutes, UserRoutes } from './Routes';
import { useAssistantMode } from '../../hooks/useAssistantMode';
import { Plus, LogOut, ChevronDown } from 'lucide-react';
//assets
import logotcnew from '../../assets/logotcnew.png';
import { MdMenu, MdClose } from 'react-icons/md';
import defaultUserImage from '../../assets/icons/ic_avatar_default.svg';
import defaultTipsterImage from '../../assets/icons/ic_tipster_default.svg';
// Custom SVG icons
import { ReactComponent as HomeIcon } from '../../assets/home.svg';
import { ReactComponent as TipsIcon } from '../../assets/tips.svg';
import { ReactComponent as SalesIcon } from '../../assets/sales.svg';
import { ReactComponent as BalanceIcon } from '../../assets/balance.svg';
//tr
import { tr } from '../../utils/translations/translator';
import { useTranslation } from 'react-i18next';

export interface MobileNavbarProps extends RouteComponentProps {
  requestLogout: () => void;
  isTipster: boolean;
  user: any;
}

const MobileNavbarComponent: React.FC<MobileNavbarProps> = (props) => {
  const { t } = useTranslation();
  const { requestLogout, isTipster, location } = props;
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const { isAssistantMode } = useAssistantMode();

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  return (
    <>
      {/* Spacer for bottom navbar */}
      <div className="h-16 md:hidden"></div>

      {/* Mobile Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex h-16 items-center justify-between px-4 shadow-xl md:hidden backdrop-blur-xl bg-[#121619]/80 border-b border-white/10">
        {/* Logo */}
        <div className="flex items-start justify-start">
          <NavLink to="/" className="flex items-center">
            <img
              src={logotcnew}
              alt="TipsterChat Logo"
              className="h-8 w-auto"
            />
          </NavLink>
        </div>

        {/* Right Side - Avatar with dropdown */}
        <div className="flex items-center gap-3">
          {/* User Avatar with dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsAvatarDropdownOpen(!isAvatarDropdownOpen)}
              className="flex items-center gap-2 rounded-lg p-1 transition-all hover:bg-gray-100/10"
            >
              <img
                src={
                  props.user.avatar_url ||
                  (isTipster ? defaultTipsterImage : defaultUserImage)
                }
                alt="Profile Avatar"
                className="h-9 w-9 rounded-full border-2 object-cover"
                style={{ borderColor: '#6633ff' }}
              />
              <ChevronDown
                className={`h-4 w-4 text-white transition-transform duration-200 ${
                  isAvatarDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {isAvatarDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-gray-100 shadow-lg border border-gray-200 py-2 z-50">
                {/* <button
                  onClick={() => {
                    setIsAvatarDropdownOpen(false);
                    // Navigate to profile
                    window.location.href = '/profile';
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <User className="h-4 w-4" />
                  {tr(t, 'profile')}
                </button> */}
                <button
                  onClick={() => {
                    setIsAvatarDropdownOpen(false);
                    requestLogout();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  {tr(t, 'logout')}
                </button>
              </div>
            )}
          </div>

          {/* Burger Menu Button - HIDDEN */}
          <button
            onClick={() => setIsBurgerOpen(!isBurgerOpen)}
            className="hidden h-10 w-10 items-center justify-center rounded-lg text-white transition-all hover:bg-gray-100/10"
            style={isBurgerOpen ? { backgroundColor: '#6633ff' } : undefined}
            aria-label="Toggle menu"
          >
            {isBurgerOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
          </button>
        </div>
      </nav>

      {/* Overlay for burger menu */}
      {isBurgerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          style={{ top: '64px' }}
          onClick={() => setIsBurgerOpen(false)}
        />
      )}

      {/* Overlay for avatar dropdown */}
      {isAvatarDropdownOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          onClick={() => setIsAvatarDropdownOpen(false)}
        />
      )}

      {/* Slide-in Menu */}
      <div
        className={`fixed left-0 right-0 z-40 transform overflow-hidden shadow-2xl transition-all duration-300 ease-in-out md:hidden ${
          isBurgerOpen
            ? 'top-16 max-h-[calc(100vh-4rem)] opacity-100'
            : 'top-16 max-h-0 opacity-0'
        }`}
        style={{ backgroundColor: '#121619' }}
      >
        <div
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 4rem)' }}
        >
          <div className="flex flex-col gap-1 p-4">
            {/* TIPSTER Routes */}
            {isTipster
              ? TipsterRoutes.map((section) => (
                  <div key={section.key} className="mb-4">
                    <div className="mb-2 flex items-center gap-2 px-3">
                      <div
                        className="h-0.5 w-4"
                        style={{ backgroundColor: '#6633ff' }}
                      ></div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                        {tr(t, section.key)}
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      {section.routes.map((route) => {
                        return (
                          route.active && !(route.assistantHidden && isAssistantMode) && (
                            <NavLink
                              key={route.key}
                              to={route.path}
                              className="block rounded-lg px-4 py-3 text-sm font-medium text-white transition-all hover:bg-gray-100/10 hover:pl-5"
                              activeClassName="font-bold"
                              activeStyle={{
                                backgroundColor: '#6633ff',
                                paddingLeft: '1.25rem',
                              }}
                              onClick={() => setIsBurgerOpen(false)}
                            >
                              {tr(t, route.key)}
                            </NavLink>
                          )
                        );
                      })}
                    </div>
                  </div>
                ))
              : UserRoutes.map((section) => (
                  <div key={section.key} className="mb-4">
                    <div className="mb-2 flex items-center gap-2 px-3">
                      <div
                        className="h-0.5 w-4"
                        style={{ backgroundColor: '#6633ff' }}
                      ></div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                        {tr(t, section.key)}
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      {section.routes.map((route: any) => {
                        return (
                          route.active && (
                            <NavLink
                              key={route.key}
                              to={route.path}
                              className="block rounded-lg px-4 py-3 text-sm font-medium text-white transition-all hover:bg-gray-100/10 hover:pl-5"
                              activeClassName="font-bold"
                              activeStyle={{
                                backgroundColor: '#6633ff',
                                paddingLeft: '1.25rem',
                              }}
                              onClick={() => setIsBurgerOpen(false)}
                            >
                              {tr(t, route.key)}
                            </NavLink>
                          )
                        );
                      })}
                    </div>
                  </div>
                ))}

            {/* Divider */}
            <div className="my-2 border-t border-white/10"></div>

            {/* Logout Button */}
            <button
              onClick={() => {
                setIsBurgerOpen(false);
                requestLogout();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-red-400 transition-all hover:bg-red-500/10"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {tr(t, 'logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar - COMPACT REDESIGN */}
      {isTipster && (
        <nav className="fixed bottom-2 left-2 right-2 z-50 md:hidden rounded-2xl shadow-2xl backdrop-blur-xl bg-[#121619]/80 border border-white/10">
          {/* Gradient border top */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#6633ff]/50 to-transparent rounded-t-2xl"></div>

          <div className={`relative grid ${isAssistantMode ? 'grid-cols-3' : 'grid-cols-5'} p-2`}>
            {/* Home */}
            <NavLink
              to="/"
              className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 transition-all duration-300 hover:scale-105"
            >
              {isActive('/') && (
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#6633ff]/20 to-[#9146ff]/20 blur-sm"></div>
              )}
              <div
                className={`relative flex h-6 w-6 items-center justify-center rounded-lg transition-all duration-300 ${
                  isActive('/')
                    ? 'bg-gradient-to-br from-[#6633ff] to-[#9146ff] shadow-md shadow-[#6633ff]/30'
                    : 'bg-gray-100/10 group-hover:bg-gray-100/20'
                }`}
              >
                <HomeIcon
                  className={`h-4 w-4 transition-all duration-300 ${
                    isActive('/')
                      ? '[&_path]:fill-white'
                      : '[&_path]:fill-gray-300 group-hover:[&_path]:fill-white'
                  }`}
                />
              </div>
              <span
                className={`text-[10px] font-semibold transition-all duration-300 ${
                  isActive('/')
                    ? 'text-white'
                    : 'text-gray-400 group-hover:text-gray-200'
                }`}
              >
                Home
              </span>
            </NavLink>

            {/* Finalizar Tip */}
            <NavLink
              to="/my-bets"
              className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 transition-all duration-300 hover:scale-105"
            >
              {isActive('/my-bets') && (
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#6633ff]/20 to-[#9146ff]/20 blur-sm"></div>
              )}
              <div
                className={`relative flex h-6 w-6 items-center justify-center rounded-lg transition-all duration-300 ${
                  isActive('/my-bets')
                    ? 'bg-gradient-to-br from-[#6633ff] to-[#9146ff] shadow-md shadow-[#6633ff]/30'
                    : 'bg-gray-100/10 group-hover:bg-gray-100/20'
                }`}
              >
                <TipsIcon
                  className={`h-4 w-4 transition-all duration-300 ${
                    isActive('/my-bets')
                      ? '[&_path]:fill-white'
                      : '[&_path]:fill-gray-300 group-hover:[&_path]:fill-white'
                  }`}
                />
              </div>
              <span
                className={`text-[10px] font-semibold transition-all duration-300 ${
                  isActive('/my-bets')
                    ? 'text-white'
                    : 'text-gray-400 group-hover:text-gray-200'
                }`}
              >
                Tips
              </span>
            </NavLink>

            {/* Publicar Tip - Simple button sin destacado */}
            <button
              onClick={() => {
                window.location.href = '/pc';
              }}
              className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 transition-all duration-300 hover:scale-105"
            >
              {/* Main button - Same style as others */}
              <div className="relative flex h-6 w-6 items-center justify-center rounded-lg bg-gray-100/10 group-hover:bg-gray-100/20 transition-all duration-300">
                <Plus
                  className="h-4 w-4 text-gray-300 group-hover:text-white transition-all duration-300"
                  strokeWidth={2.5}
                  style={{ fill: 'none' }}
                />
              </div>
              <span className="text-[10px] font-semibold text-gray-400 group-hover:text-gray-200 transition-all duration-300">
                Publicar
              </span>
            </button>

            {/* Ventas */}
            {!isAssistantMode && (
              <NavLink
                to="/sales"
                className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 transition-all duration-300 hover:scale-105"
              >
                {isActive('/sales') && (
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#6633ff]/20 to-[#9146ff]/20 blur-sm"></div>
                )}
                <div
                  className={`relative flex h-6 w-6 items-center justify-center rounded-lg transition-all duration-300 ${
                    isActive('/sales')
                      ? 'bg-gradient-to-br from-[#6633ff] to-[#9146ff] shadow-md shadow-[#6633ff]/30'
                      : 'bg-gray-100/10 group-hover:bg-gray-100/20'
                  }`}
                >
                  <SalesIcon
                    className={`h-4 w-4 transition-all duration-300 ${
                      isActive('/sales')
                        ? '[&_path]:fill-white'
                        : '[&_path]:fill-gray-300 group-hover:[&_path]:fill-white'
                    }`}
                  />
                </div>
                <span
                  className={`text-[10px] font-semibold transition-all duration-300 ${
                    isActive('/sales')
                      ? 'text-white'
                      : 'text-gray-400 group-hover:text-gray-200'
                  }`}
                >
                  Ventas
                </span>
              </NavLink>
            )}

            {/* Retirar */}
            {!isAssistantMode && (
              <NavLink
                to="/balance"
                className="group relative flex flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 transition-all duration-300 hover:scale-105"
              >
                {isActive('/balance') && (
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#6633ff]/20 to-[#9146ff]/20 blur-sm"></div>
                )}
                <div
                  className={`relative flex h-6 w-6 items-center justify-center rounded-lg transition-all duration-300 ${
                    isActive('/balance')
                      ? 'bg-gradient-to-br from-[#6633ff] to-[#9146ff] shadow-md shadow-[#6633ff]/30'
                      : 'bg-gray-100/10 group-hover:bg-gray-100/20'
                  }`}
                >
                  <BalanceIcon
                    className={`h-4 w-4 transition-all duration-300 ${
                      isActive('/balance')
                        ? '[&_path]:fill-white'
                        : '[&_path]:fill-gray-300 group-hover:[&_path]:fill-white'
                    }`}
                  />
                </div>
                <span
                  className={`text-[10px] font-semibold transition-all duration-300 ${
                    isActive('/balance')
                      ? 'text-white'
                      : 'text-gray-400 group-hover:text-gray-200'
                  }`}
                >
                  Retirar
                </span>
              </NavLink>
            )}
          </div>
        </nav>
      )}
    </>
  );
};

export const MobileNavbar = withRouter(MobileNavbarComponent);
